import Cookies from 'universal-cookie';
import { globalizationPath } from '@rcg/ui-react';
import {
  BRAND,
  CACHE_PERSIST,
  COOKIE_KEYS,
  NAVIGATION_PATHS,
  UNDEFINED_GUESTS,
  SESSION_STORAGE_BOOKING_TOKEN_NAME,
  ALLOWED_CEL_REDIRECT_ISO_COUNTRIES,
} from './constants';
import { getSessionStorageItem } from '../operations/sessionStorage/utils';
import { ReactComponent as WarningIcon } from '../assets/svg/warningIcon.svg';
import { ReactComponent as CheckIcon } from '../assets/svg/checkmark_white.svg';
import theme from './theme';
import getBrand from './getBrand';

const cookies = new Cookies({ path: '/' });
const findErrorCode = (error, code) => {
  if (typeof code === 'string') {
    return error.graphQLErrors.find(({ extensions }) => extensions.code === code);
  }
  return error.graphQLErrors.find(({ extensions }) => code.includes(extensions.code));
};
export const findErrorMessage = (error, message) => {
  return error.graphQLErrors.find(({ extensions }) =>
    extensions?.exception?.message.includes(message),
  );
};
const getRootSubDomain = () => {
  const locOrigin = window.location.hostname;
  let locOriginMatch = locOrigin.match(/.*(\.[^.]+\.[^.]+\.com\.[^.]+)$/);
  if (locOriginMatch == null) {
    locOriginMatch = locOrigin.match(/.*(\.[^.]+\.com\.[^.]+)$/);
  }
  if (locOriginMatch == null) {
    locOriginMatch = locOrigin.match(/.*(\.[^.]+\.[^.]+\.[^.]+)$/);
  }
  if (locOriginMatch == null) {
    locOriginMatch = locOrigin.match(/.*(\.[^.]+\.[^.]+)$/);
  }

  if (locOriginMatch != null) {
    return locOriginMatch[1];
  }
  return locOrigin;
};
export const generateRelativeUrl = (endpoint) => {
  const { origin } = window.location;
  if (origin.includes('localhost') || origin.includes('0.0.0.0')) {
    return endpoint;
  }
  return `${origin}${endpoint}`;
};

export const createCookie = ({ name, value, time = null }) => {
  const domain = getRootSubDomain();
  const options = { path: '/', domain, secure: true };
  if (time !== null) {
    options.expires = time;
  }
  cookies.set(name, value, options);
};
export const setSessionStorageItem = (key, data) => {
  sessionStorage.setItem(key, JSON.stringify(data));
};

export const getCelebrityUrl = (destinationPath = NAVIGATION_PATHS.manageVacations) => {
  const searchValues = getSessionStorageItem('search-values');
  const bookingProfileData = getSessionStorageItem('booking-profile');
  const valuesFromProfile = {
    bookingId: bookingProfileData?.currentBooking?.id,
    ship: bookingProfileData?.currentBooking?.sailing?.itinerary?.ship?.code,
    sailDate: bookingProfileData?.currentBooking?.sailing?.sailDate,
    lastName: bookingProfileData?.currentBooking?.guests?.[0].lastName,
  };

  const { bookingId, lastName, sailDate, ship } = searchValues?.bookingId
    ? searchValues
    : valuesFromProfile || {};
  return `${destinationPath}?bid=${bookingId}&sdt=${sailDate}&scd=${ship}&lnm=${lastName}&gic=true`;
};

export const getLinkInformation = (currentUrl, isCelebrityBrand, linkInfo) => {
  return isCelebrityBrand ? getCelebrityUrl(linkInfo) : currentUrl;
};

export const emptyFunction = () => {
  // This is intentional
};

const WWW_PREFIX = '';

export const getCookies = (key) => cookies.get(key);

export const encodeToken = (token) => {
  if (!token) return '';
  return encodeURIComponent(token);
};

export const getAmendAuthRedirection = (prefixPathVar, isCelebrityBrand, ldFlags) => {
  const enableRedirect = ldFlags ? ldFlags?.['is-unified-manage-redirection-enabled'] : null;

  if (!enableRedirect) {
    const loyaltyData = getCookies(COOKIE_KEYS.LOYALTY_DATA);
    const targetPage = `${prefixPathVar()}${
      loyaltyData ? NAVIGATION_PATHS.reservationLookup : NAVIGATION_PATHS.reservation
    }`;
    return getLinkInformation(targetPage, isCelebrityBrand);
  }

  const currentToken = getSessionStorageItem(SESSION_STORAGE_BOOKING_TOKEN_NAME, false);
  return `/manage/api/v1/bookings/redirect?cipher=${encodeToken(currentToken)}`;
};

const isWwwCookieExists = (cookieDomains) => {
  return cookieDomains.length === 0
    ? false
    : cookieDomains.filter((x) => x.includes(WWW_PREFIX, 0)).length === 0;
};

const addMissingWwwCookieDomain = (cookieDomains, fullhostname) => {
  const isWwwCookieDomainMissing = isWwwCookieExists(cookieDomains);
  if (isWwwCookieDomainMissing) {
    cookieDomains.push(`${WWW_PREFIX}${fullhostname}`);
  }
  return cookieDomains;
};
const getArrFullHostNames = (fullhostname) => {
  return fullhostname.split('.').reduce(
    (acc, _, idx, arr) => {
      if (idx > 0 && idx < arr.length - 1) {
        const value = isWwwCookieExists(acc)
          ? arr.slice(idx).join('.')
          : `.${arr.slice(idx).join('.')}`;
        acc.push(value);
      }
      return acc;
    },
    [fullhostname],
  );
};

const returnookieDomains = () => {
  const fullhostname = window.location.hostname;
  const arrFullHostNames = getArrFullHostNames(fullhostname);
  return addMissingWwwCookieDomain(arrFullHostNames, fullhostname);
};

const removeCookie = (key) => {
  const cookieDomains = returnookieDomains();
  cookieDomains.forEach((domain) => {
    document.cookie = `${key}=;expires=1970-09-25T22:27:16.000Z;max-age=0;path=/;domain=${domain};`;
  });
};

const removeCookieKey = () => {
  cookies.remove(COOKIE_KEYS.LOYALTY_DATA, { path: '/' });
  cookies.remove(COOKIE_KEYS.ACCESS_TOKEN, { path: '/' });
};

const localRemoveCookie = () => {
  removeCookie(COOKIE_KEYS.ACCESS_TOKEN);
  removeCookie(COOKIE_KEYS.LOYALTY_DATA);
};
const clearStorage = (client) => {
  client.clearStore();
  sessionStorage.clear();
};

export const signOffProfile = (client) => {
  removeCookieKey();
  localRemoveCookie();
  clearStorage(client);
};

const fliyingFromOptions = ({ name, city, code }) => {
  if (name?.includes(city)) {
    return `${name} (${code})`;
  }
  return `${city?.concat(' ', name)} (${code})`;
};

export const getDurationLabel = (totalDurationMins) => {
  const minutes = totalDurationMins % 60;
  const hours = Math.floor(totalDurationMins / 60);
  const minutesLabel = minutes === 0 ? '' : `${minutes}m`;
  const hoursLabel = hours === 0 ? '' : `${hours}h`;
  return `${hoursLabel} ${minutesLabel}`;
};

export const getStopsCodes = (stops) => {
  // return flight scales airport codes form an array of flightsegments
  const stopsCodes = [];
  stops.forEach((stop, index) => {
    if (index > 0) {
      stopsCodes.push(stop.departure.airport.code);
    }
  });
  return stopsCodes;
};

const layer = (numberOfStops, renderContent) => {
  if (numberOfStops === 0) {
    return renderContent('flightSummaryNonstopLabel', 'Nonstop');
  }
  if (numberOfStops === 1)
    return `${numberOfStops} ${renderContent('flightSummaryStopInLabel', 'stop in ')} `;
  return `${numberOfStops} ${renderContent('flightSummaryStopsInLabel', 'stops in ')} `;
};

export const layover = (numberOfStops, renderContent) => {
  return layer(numberOfStops, renderContent);
};
const getFinalAiport = (segments) => {
  // eslint-disable-next-line consistent-return
  segments.forEach((segment, index) => {
    if (index === segments.length - 1) {
      return segment.arrival.airport.code;
    }
  });
  return null;
};
export const lastDestination = (segments) => {
  return getFinalAiport(segments);
};

export const cacheClean = () => {
  localStorage.clear(CACHE_PERSIST);
};

export const getAirlines = (flightSegments) => {
  const allAirlines = flightSegments.map((segment) => segment.operatingAirline.name);
  const uniqueAirlines = new Set(allAirlines);

  return [...uniqueAirlines];
};

export const notificationColor = (notificationType) => {
  const colorMapping = {
    warning: theme.palette.error.warning,
    error: theme.palette.error.main,
  };
  return colorMapping[notificationType] || theme.palette.green;
};

export const notificationIcon = (notificationType) => {
  // eslint-disable-next-line
  if (notificationType === 'error') return <WarningIcon />;
  // eslint-disable-next-line
  return <CheckIcon />;
};

export function formatDateRange(startDate, endDate, localizedDayjs) {
  const endDay = localizedDayjs(endDate);
  const startDay = localizedDayjs(startDate);

  if (startDay.isSame(endDay)) {
    // Same date oneWay scenario, format: Apr 29, 2024
    return {
      startDate: `${startDay.format('MMM DD')}, ${startDay.format('YYYY')}`,
      endDate: '',
    };
  }

  if (startDay.year() === endDay.year()) {
    // Same year, format: Apr 29 - May 04, 2024
    return {
      startDate: `${startDay.format('MMM DD')}`,
      endDate: `${endDay.format('MMM DD')}, ${startDay.format('YYYY')}`,
    };
  }
  // Different years, format: Apr 29, 2024 - May 04, 2024
  return {
    startDate: `${startDay.format('MMM DD')}, ${startDay.format('YYYY')}`,
    endDate: `${endDay.format('MMM DD')}, ${endDay.format('YYYY')}`,
  };
}

export function getUniqueConfirmationNumbers(flights) {
  const uniqueConfirmationNumbers = new Set();
  flights?.forEach((flight) => {
    const { confirmationNumbers } = flight;

    confirmationNumbers?.forEach((confirmationNumber) => {
      uniqueConfirmationNumbers?.add(confirmationNumber);
    });
  });
  return Array.from(uniqueConfirmationNumbers);
}

export const getRouteType = (flightRoutes, routeNames) => {
  if (flightRoutes.length === 2) {
    const departures = flightRoutes.map((direction) => {
      return direction.flightSegments[0].departure.airport.code;
    });

    const returnings = flightRoutes.map((direction) => {
      return direction.flightSegments.slice(-1)[0].arrival.airport.code;
    });

    if (departures[0] === returnings[1] && departures[1] === returnings[0]) {
      return routeNames.roundtrip;
    }
    return routeNames.multiCity;
  }
  if (flightRoutes.length === 1) return routeNames.oneWay;
  return null;
};

export const checkForSomeFlights = (flightProfile) => {
  return flightProfile?.guests?.some((guest) => {
    return guest?.flightGuest?.guestFlights?.length > 0;
  });
};
export const getFlightsBook = (flightProfile) => {
  return flightProfile?.guests?.every(({ flightGuest }) => flightGuest?.guestFlights);
};
export const hasGuestUndefined = (guests) =>
  guests?.some((guest) => {
    return (
      Object.values(UNDEFINED_GUESTS).includes(guest.firstName) ||
      Object.values(UNDEFINED_GUESTS).includes(guest.lastName)
    );
  });

export const setSessionTokenFromUrl = () => {
  const urlTokenParam = /token=([^&]+)/i.exec(window.location.search);
  const urlBookingToken = Array.isArray(urlTokenParam) && urlTokenParam[1];
  if (urlBookingToken) {
    sessionStorage.setItem(SESSION_STORAGE_BOOKING_TOKEN_NAME, urlBookingToken);
  }
};

export const snakeToCamel = (snakeCaseStr) => {
  return snakeCaseStr.replace(/(_\w)/g, (match) => match[1].toUpperCase());
};

export const appendGlobalizationToPath = (pathUrl, globalData) => {
  const { market, lang, isoCode, country } = globalData;
  const marketLangPath = globalizationPath(market, lang, '');
  const isCelebrity = getBrand()?.id === BRAND.CELEBRITY;

  if (!isCelebrity) {
    return `${marketLangPath}${pathUrl}`;
  }

  if (
    (country.toUpperCase() === 'CAN' || country.toUpperCase() === 'USA') &&
    lang.toLowerCase() === 'fr'
  ) {
    return `/ca/fr${pathUrl}`;
  }
  if (ALLOWED_CEL_REDIRECT_ISO_COUNTRIES.includes(country.toUpperCase())) {
    return `/${isoCode.toLowerCase()}${pathUrl}`;
  }

  return pathUrl;
};

export default {
  findErrorCode,
  emptyFunction,
  fliyingFromOptions,
  getDurationLabel,
  getStopsCodes,
  layover,
  lastDestination,
  getAirlines,
  formatDateRange,
  getUniqueConfirmationNumbers,
};
